import React, { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { BRANCHES, DEFAULT_PRODUCTION_URL } from "@/lib/branches";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Download, Check } from "lucide-react";

function getBaseUrl(savedUrl) {
  return (savedUrl || DEFAULT_PRODUCTION_URL).replace(/\/$/, "");
}

export default function QRCodesTab() {
  const [baseUrl, setBaseUrl] = useState(() => {
    const saved = localStorage.getItem("kg_qr_base_url");
    // Auto-migrate the previous default URL to the new one
    if (saved === "https://konattuluckydraw.konattugroup.in") {
      localStorage.setItem("kg_qr_base_url", DEFAULT_PRODUCTION_URL);
      return DEFAULT_PRODUCTION_URL;
    }
    return saved || DEFAULT_PRODUCTION_URL;
  });
  const [counts, setCounts] = useState({});
  const [qrUrls, setQrUrls] = useState([]);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const loadCounts = async () => {
    try {
      const { data } = await apiClient.get("/summary");
      const map = {};
      data.rows.forEach(r => { map[r.branch] = r.entries; });
      setCounts(map);
    } catch (_e) { /* noop */ }
  };

  useEffect(() => { loadCounts(); }, []);

  // Generate QR data URLs whenever baseUrl changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const base = getBaseUrl(baseUrl);
      const out = await Promise.all(BRANCHES.map(async (b, i) => {
        const url = `${base}/?b=${i}`;
        const dataUrl = await QRCode.toDataURL(url, {
          width: 360,
          margin: 1,
          color: { dark: "#CC0000", light: "#FFFFFF" },
          errorCorrectionLevel: "M",
        });
        return { branch: b, url, dataUrl };
      }));
      if (!cancelled) setQrUrls(out);
    })();
    return () => { cancelled = true; };
  }, [baseUrl]);

  const saveUrl = () => {
    localStorage.setItem("kg_qr_base_url", baseUrl);
    toast.success("URL saved");
  };

  const copyLink = async (url, idx) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(null), 1500);
    } catch { toast.error("Copy failed"); }
  };

  const downloadBranded = async (branch, dataUrl) => {
    // Compose branded PNG: white bg, red title, QR, branch name
    const W = 800, H = 1000;
    const canvas = document.createElement("canvas");
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, W, H);

    // Red top stripe
    ctx.fillStyle = "#CC0000";
    ctx.fillRect(0, 0, W, 12);

    // Title
    ctx.fillStyle = "#CC0000";
    ctx.font = "bold 36px 'Playfair Display', Georgia, serif";
    ctx.textAlign = "center";
    ctx.fillText("KONATTU GROUP", W / 2, 90);
    ctx.fillStyle = "#1A1A1A";
    ctx.font = "600 22px 'DM Sans', Arial, sans-serif";
    ctx.fillText("LUCKY DRAW", W / 2, 130);

    // Card frame
    ctx.strokeStyle = "#EAEAEA";
    ctx.lineWidth = 2;
    ctx.strokeRect(80, 170, W - 160, 640);

    // Load QR
    const img = new Image();
    img.crossOrigin = "anonymous";
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = dataUrl;
    });
    const qrSize = 500;
    ctx.drawImage(img, (W - qrSize) / 2, 200, qrSize, qrSize);

    // Branch name
    ctx.fillStyle = "#1A1A1A";
    ctx.font = "bold 28px 'DM Sans', Arial, sans-serif";
    ctx.textAlign = "center";
    // Wrap if too long
    const words = branch.split(" ");
    const lines = [];
    let cur = "";
    for (const w of words) {
      const test = cur ? cur + " " + w : w;
      if (ctx.measureText(test).width > W - 200 && cur) {
        lines.push(cur); cur = w;
      } else { cur = test; }
    }
    if (cur) lines.push(cur);
    lines.forEach((ln, i) => {
      ctx.fillText(ln, W / 2, 740 + i * 32);
    });

    // Footer red stripe
    ctx.fillStyle = "#CC0000";
    ctx.fillRect(0, H - 8, W, 8);

    // Footer text
    ctx.fillStyle = "#888";
    ctx.font = "400 14px 'DM Sans', Arial, sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Scan this QR with your phone camera to enter today's draw", W / 2, H - 30);

    const png = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = png;
    a.download = `konattu-qr-${branch.replace(/[^a-z0-9]+/gi, "-")}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-6" data-testid="qr-tab">
      <div className="kg-card kg-card-red-top p-6">
        <h3 className="font-display text-2xl text-neutral-900">QR Codes per Branch</h3>
        <p className="text-sm text-neutral-600 mt-1">
          QR codes encode <code className="bg-neutral-100 px-1 rounded">{getBaseUrl(baseUrl)}/?b=[index]</code>
        </p>

        <div className="mt-4 flex flex-col md:flex-row gap-3 md:items-end">
          <div className="flex-1">
            <label className="text-sm text-neutral-700">Deployed URL</label>
            <Input
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              className="kg-input mt-2 h-12 border-2"
              placeholder="https://luckydraw.konattugroup.com"
              data-testid="qr-base-url-input"
            />
          </div>
          <Button onClick={saveUrl} className="kg-btn-red h-12" data-testid="qr-save-url">Save URL</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {qrUrls.map((q, i) => (
          <div key={i} className="kg-card kg-card-red-top p-5" data-testid={`qr-card-${i}`}>
            <p className="text-sm font-semibold text-neutral-900">{q.branch}</p>
            <p className="text-xs text-neutral-500 mt-1">Today&apos;s entries: {counts[q.branch] || 0}</p>
            <div className="mt-3 bg-white border border-neutral-200 rounded-lg p-3 flex justify-center">
              <img src={q.dataUrl} alt={`QR ${q.branch}`} className="w-44 h-44" />
            </div>
            <p className="text-xs text-neutral-500 mt-2 truncate" title={q.url}>{q.url}</p>
            <div className="mt-3 flex gap-2">
              <Button
                variant="outline"
                size="sm"
                className="flex-1 border-[#CC0000] text-[#CC0000] hover:bg-[#FFE9E9]"
                onClick={() => downloadBranded(q.branch, q.dataUrl)}
                data-testid={`qr-download-${i}`}
              >
                <Download className="w-4 h-4 mr-1" /> PNG
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="flex-1"
                onClick={() => copyLink(q.url, i)}
                data-testid={`qr-copy-${i}`}
              >
                {copiedIdx === i ? <><Check className="w-4 h-4 mr-1" /> Copied</> : <><Copy className="w-4 h-4 mr-1" /> Link</>}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
